import { Col, Form, FormInstance, notification, Row, Select, Spin } from 'antd';
import { useIntl } from 'react-intl';

import { FloatingFormItem } from '@/components/_common';
import { DirectSaleStockItem } from '@/core/domain/sales/DirectSale/DirectSale.types';
import {
  ListStockItemsQuery,
  PartStockPricesResult,
  StockItem,
  StockItemType,
} from '@/core/domain/stock/StockItems/StockItems.types';
import { sortBy } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import StockItemsModal from '../../StockCorrections/StockItemsModal';

import InputSearch from '@/components/_common/InputSearch';
import { useMatrix } from '@/core/domain/enterprise/Matrix/Matrix.hooks';
import {
  usePartStockPricesMutation,
  useStockItems,
} from '@/core/domain/stock/StockItems/StockItems.hooks';
import { RegistrationsDefaultCode } from '@/types/Register';
import DirectSaleAddPartPriceForm from './DirectSaleAddPartPriceForm';
import { flatInfiniteResult } from '@/utils/request/flatInfiniteResult';
import { SearchOutlined, CloseOutlined } from '@ant-design/icons';

interface Props {
  form?: FormInstance<DirectSaleStockItem & { code?: string }>;
  companyId?: number;
  partsSelected?: number[];
  directSaleItem?: DirectSaleStockItem;
  onPartSelected: (part: StockItem) => void;
  stockPricesItemEdit?: PartStockPricesResult;
  isPartSelected: boolean;
  setIsPartSelected: (state: boolean) => void;
}
const DirectSaleAddPartForm = ({
  form,
  companyId,
  partsSelected,
  directSaleItem,
  onPartSelected,
  stockPricesItemEdit,
  isPartSelected,
  setIsPartSelected,
}: Props) => {
  const intl = useIntl();

  const [showSelect, setShowSelect] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number>();
  const [stockItemsModalVisible, setStockItemsModalVisible] = useState(false);
  const [queryManufacturing, setQueryManufacturing] =
    useState<ListStockItemsQuery>({ manufacturingCode: '' });
  const [matrixConfig, setMatrixConfig] = useState<RegistrationsDefaultCode>();

  const [avaliableQuantityItem, setAvaliableQuanittyItem] = useState<number>();

  const partMutation = usePartStockPricesMutation();

  const { data, isFetching } = useStockItems(true, {
    ...queryManufacturing,
    itemType: StockItemType.Part,
  });

  const infiniteData = flatInfiniteResult(data);

  useEffect(() => {
    if (stockPricesItemEdit) {
      const avaliableQuantity =
        (directSaleItem?.totalStock ?? 0) + stockPricesItemEdit.availableStock;

      setAvaliableQuanittyItem(avaliableQuantity);
    }
  }, [stockPricesItemEdit]);

  const { data: matrixData } = useMatrix();

  useEffect(() => {
    if (matrixData) {
      setMatrixConfig(matrixData.matrixSettings.defaultPartCode);
    }
  }, [matrixData]);

  const filteredInfinite = infiniteData.filter((cur) => {
    if (companyId) {
      if (cur.stock) {
        const finder = cur.stock.find(
          (stock) => stock.company.id === companyId,
        );
        return !!finder && finder.availableStock >= 1;
      }

      return 0;
    }
  });

  const handleStockItemsModalVisible = (): void => {
    if (!isPartSelected) {
      form?.resetFields();
      setShowSelect(false);
      form?.setFieldValue('code', undefined);
      setIsPartSelected(true);
    } else if (companyId) {
      setSelectedCompanyId(companyId);

      setStockItemsModalVisible(!stockItemsModalVisible);
    } else {
      notification.error({
        message: intl.formatMessage({
          id: 'directSale.modal.form.open.emptyCompanyAndCustomerError',
        }),
        description: intl.formatMessage({
          id: 'directSale.modal.form.open.emptyCompanyAndCustomerDescription',
        }),
      });
    }
  };

  const codeKey: Record<RegistrationsDefaultCode, keyof StockItem> = {
    [RegistrationsDefaultCode.barCode]: 'barcode',
    [RegistrationsDefaultCode.companyCode]: 'companyCode',
    [RegistrationsDefaultCode.internalCode]: 'sku',
    [RegistrationsDefaultCode.sku]: 'sku',
    [RegistrationsDefaultCode.manufacturerCode]: 'manufacturingCode',
  };

  const handleSelectPart = async (part: StockItem) => {
    const sorted = sortBy(part.prices, (a) => a.company.id);
    const matrixPrice = sorted.find((item) => item.company.id === companyId);

    form?.setFieldsValue({
      code: matrixConfig
        ? `${(part[codeKey[matrixConfig]] as string) || '-'} | ${
            part.description
          }`
        : part.manufacturingCode,
      ...part,
      manufacturerId: part.manufacturer?.id,
      unitaryValue: matrixPrice?.price,
      unitCost: matrixPrice?.realCost,
      totalStock: 1,
      companyCode: part.companyCode,
    });

    const response = await partMutation.mutateAsync(part.partId);

    onPartSelected(part);

    if (response) {
      const availableStock = response.result?.find(
        (cur) => cur.company.id === companyId,
      )?.availableStock;

      setAvaliableQuanittyItem(availableStock);
    }

    setStockItemsModalVisible(!stockItemsModalVisible);
  };

  const handleCodeSearch = (code: string) => {
    setShowSelect(code.length >= 1);
    matrixConfig === RegistrationsDefaultCode.barCode
      ? setQueryManufacturing((prev) => ({ ...prev, barcode: code }))
      : matrixConfig === RegistrationsDefaultCode.companyCode
      ? setQueryManufacturing((prev) => ({ ...prev, CompanyCode: code }))
      : matrixConfig === RegistrationsDefaultCode.internalCode
      ? setQueryManufacturing((prev) => ({ ...prev, sku: code }))
      : setQueryManufacturing((prev) => ({ ...prev, manufacturingCode: code }));
  };

  const handleSetDescription = (partId: number) => {
    const findStockItem = infiniteData.find((cur) => cur.partId === partId);
    const item = findStockItem?.prices.find(
      (cur) => cur.company.id === companyId,
    );
    form?.setFieldsValue({
      description: findStockItem?.description,
      manufacturerId: findStockItem?.manufacturer?.id,
      unitaryValue: item?.price,
      unitCost: item?.realCost,
      totalStock: 1,
      partId,
      companyCode: findStockItem?.companyCode,
    });
    if (findStockItem) {
      const availableStock = findStockItem.stock?.find(
        (cur) => cur.company.id === companyId,
      )?.availableStock;

      setAvaliableQuanittyItem(availableStock || 0);

      availableStock === 0 && form?.setFieldValue('totalStock', 0);

      onPartSelected(findStockItem);
      setShowSelect(false);
    }
  };

  const getSelectableOptions = useCallback(() => {
    const unselectedParts = filteredInfinite.filter(
      (item) =>
        !partsSelected?.some((selectedItem) => selectedItem === item.partId),
    );

    return unselectedParts.map((part) => ({
      label:
        matrixConfig === RegistrationsDefaultCode.barCode
          ? `${part?.barcode || '-'} | ${part.description}`
          : matrixConfig === RegistrationsDefaultCode.companyCode
          ? `${part.companyCode || '-'} | ${part.description}`
          : matrixConfig === RegistrationsDefaultCode.internalCode
          ? `${part.sku || '-'} | ${part.description}`
          : `${part.manufacturingCode || '-'} | ${part.description}`,
      value: part.partId,
    }));
  }, [filteredInfinite]);

  return (
    <Form layout="vertical" form={form}>
      <Row gutter={24}>
        <Col span={24} lg={8} md={8}>
          <FloatingFormItem
            shouldUpdate
            label={intl.formatMessage({
              id: `directSale.modal.form.${
                matrixConfig === RegistrationsDefaultCode.barCode
                  ? 'barCode'
                  : matrixConfig === RegistrationsDefaultCode.companyCode
                  ? 'companyCode'
                  : matrixConfig === RegistrationsDefaultCode.internalCode
                  ? 'internalCode'
                  : 'manufacturerCode'
              }`,
            })}
            name="code"
          >
            <Select
              disabled={!isPartSelected}
              allowClear
              showSearch
              open={showSelect}
              onBlur={() => setShowSelect(false)}
              filterOption={(input, _) => {
                return input.length >= 1;
              }}
              showArrow={false}
              notFoundContent={isFetching ? <Spin /> : false}
              onSelect={handleSetDescription}
              loading={isFetching}
              onSearch={handleCodeSearch}
              options={getSelectableOptions()}
            />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={16} lg={16}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'directSale.modal.form.description',
            })}
            name="description"
          >
            <InputSearch
              buttonProps={{
                onClick: handleStockItemsModalVisible,
                disabled: !!directSaleItem?.id,
                icon: isPartSelected ? (
                  <SearchOutlined style={{ fontSize: 20 }} />
                ) : (
                  <CloseOutlined style={{ fontSize: 20 }} />
                ),
              }}
              inputProps={{ disabled: true }}
            />
          </FloatingFormItem>
        </Col>

        <Col span={24}>
          <DirectSaleAddPartPriceForm
            companyId={companyId}
            avaliableQuantityItem={avaliableQuantityItem}
            form={form}
            isPartSelected={isPartSelected}
            partStockPrices={partMutation}
            sellingPriceItemSelectedEdition={stockPricesItemEdit?.sellPrice}
          />
        </Col>
      </Row>

      <Form.Item hidden name="partId" />
      <Form.Item hidden name="unitCost" />
      <Form.Item hidden name="companyCode" />
      <Form.Item hidden name="manufacturingCode" />

      <StockItemsModal
        companyId={selectedCompanyId}
        visible={stockItemsModalVisible}
        handleVisible={handleStockItemsModalVisible}
        itemType={StockItemType.Part}
        onSelectPart={handleSelectPart}
        partsSelected={partsSelected}
        directSale
        selectedItems={[]}
        noSelection
      />
    </Form>
  );
};

export default DirectSaleAddPartForm;
